import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import Navbar from "../navbar/navbar";
import Datatable from "../../datatable/Datatable";
import Swal from "sweetalert2";
import { getBase64ImageFromUrl, getPeriodicExcelReport, getPeriodicReportList } from "../../services/receipt.service";
import { PeriodicReportPrint } from "../../print/periodicReportPrint";
import headerLogo from "../../assets/images/DailyPoojaReport.png"
import "./reportModule.css";
import { ReportExportActions, ReportListHeading, ReportListLoading } from "./ReportListUi";


const PeriodicalReportComponent = () => {

    const [fromDate, setFromDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1); // 1st of current month
    });

    const [toDate, setToDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
    });

    const [periodicList, setPeriodicList] = useState([]);
    const [loading, setLoading] = useState(true);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatISODate = (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        fetchPeriodicReportList(fromDate, toDate);
    }, [fromDate, toDate]);

    const fetchPeriodicReportList = async (from, to) => {
        setLoading(true);
        try {
            const fromDate = formatISODate(from);
            const toDate = formatISODate(to);
            const response = await getPeriodicReportList(fromDate, toDate);
            if (response) {
                setPeriodicList(response);
            } else {
                Swal.fire("Error", "Failed to fetch periodic report list", "error");
            }
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Something went wrong", "error");
        } finally {
            setLoading(false);
        }
    };

    const grandTotal = periodicList.reduce((sum, item) => sum + (item.totalAmount || 0), 0);

    const columns = [
        { name: "SN", selector: (row) => row.sn, sortable: true, align: 'text-center' },
        { name: "Receipt No", selector: (row) => row.receiptNo, sortable: true, align: 'text-start' },
        { name: "Receipt Date", selector: (row) => row.receiptDate, sortable: true, align: 'text-start' },
        { name: "Seva Name1", selector: (row) => row.poojaName, sortable: true, align: 'text-start' },
        { name: "Seva Date1", selector: (row) => row.poojaDateTime1, sortable: true, align: 'text-start' },
        { name: "Seva Name2", selector: (row) => row.poojaName2, sortable: true, align: 'text-start' },
        { name: "Seva Date2", selector: (row) => row.poojaDateTime2, sortable: true, align: 'text-start' },
        { name: "Total Persons", selector: (row) => row.totalPerson, sortable: true, align: 'text-start' },
        { name: "Total Amount", selector: (row) => row.totalAmount, sortable: true, align: 'text-start' },
    ];

    const mappedData = () => {
        return periodicList.map((item, index) => ({
            sn: index + 1,
            receiptNo: item.receiptNo || '-',
            receiptDate: formatDate(item.receiptDate) || '-',
            poojaName: item.poojaName || '-',
            poojaDateTime1: formatDate(item.poojaDateTime1) || '-',
            poojaName2: item.poojaName2 || '-',
            poojaDateTime2: formatDate(item.poojaDateTime2) || '-',
            totalPerson: item.totalPerson || '-',
            totalAmount: item.totalAmount || '-',
        }))
    }

    const handlePrint = async () => {
        const base64Logo = await getBase64ImageFromUrl(headerLogo);
        await PeriodicReportPrint(fromDate, toDate, base64Logo, periodicList);
    };

    const handleDownload = async () => {
        const date1 = formatISODate(fromDate);
        const date2 = formatISODate(toDate);
        const response = await getPeriodicExcelReport(date1,date2);
        const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "periodicReport.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="card report-list-page">
            <Navbar />
            <ReportListHeading title="Periodic Report List" />
            <div className="report-list-toolbar rowHeadercolor">
                <div className="report-list-filters col-md-8 d-flex">
                    <div className="d-flex align-items-center">
                        <label className="font-label me-2 mb-0">From :</label>
                        <DatePicker
                            selected={fromDate}
                            onChange={(newValue) => {
                                setFromDate(newValue);
                            }}
                            className="form-control"
                            placeholderText="From Date"
                            dateFormat="dd-MM-yyyy"
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            onKeyDown={(event) => event.preventDefault()}
                        />
                    </div>

                    <div className="d-flex align-items-center">
                        <label className="font-label me-2 mb-0">To :</label>
                        <DatePicker
                            selected={toDate}
                            onChange={(newValue) => {
                                setToDate(newValue);
                            }}
                            className="form-control"
                            placeholderText="To Date"
                            dateFormat="dd-MM-yyyy"
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            onKeyDown={(event) => event.preventDefault()}
                        />
                    </div>
                </div>
                <ReportExportActions onPrint={handlePrint} onDownload={handleDownload} disabled={loading} />
            </div>
            <div id="card-body customized-card" className="report-list-table">
                {loading ? <ReportListLoading message="Loading periodic report..." /> : <Datatable columns={columns} data={mappedData()} />}
            </div>
            <div className="report-list-summary">
                <h5 className="fw-bold">Grand Total: ₹ {grandTotal.toFixed(2)}</h5>
            </div>
        </div>
    );
}

export default PeriodicalReportComponent;